/**
 * @fileoverview SnackbarNotification Component
 *
 * This is to manage snackbar notifications
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import announceNotification from '../../utils/announce-notification';

const OPENING = 'lta-snackbar-notification--opening';
const OPEN = 'lta-snackbar-notification--open';
const CLOSING = 'lta-snackbar-notification--closing';

class SnackbarNotification {
	constructor(element, options) {
		this.numbers = {
			/**
			 * Number of milliseconds to wait between temporarily clearing the label text
			 * in the DOM and subsequently restoring it. This is necessary to force IE 11
			 * to pick up the `aria-live` content change and announce it to the user.
			 */
			ARIA_LIVE_DELAY_MS: 1000,
			DEFAULT_AUTO_DISMISS_TIMEOUT_MS: 1500,
			INDETERMINATE: -1,
			// These variables need to be kept in sync with the values in scss.
			SNACKBAR_ANIMATION_CLOSE_TIME_MS: 300,
			SNACKBAR_ANIMATION_OPEN_TIME_MS: 300
		};

		this.labels = {
			ARIA_LIVE_LABEL_TEXT_ATTR: 'data-snackbar-label-text',
			REASON_DISMISS: 'dismiss'
		};

		this.isOpen_ = false;
		this.animationFrame_ = 0;
		this.animationTimer_ = 0;
		this.autoDismissTimer_ = 0;

		this.options = {
			onOpening: options.onOpening || function () {},
			onOpen: options.onOpen || function () {},
			onClosing: options.onClosing || function () {},
			onClosed: options.onClosing || function () {},
			openingTimeout: options.openingTimeout || this.numbers.SNACKBAR_ANIMATION_OPEN_TIME_MS,
			openTimeout: options.timeout || this.numbers.DEFAULT_AUTO_DISMISS_TIMEOUT_MS,
			closingTimeout: options.closingTimeout || this.numbers.SNACKBAR_ANIMATION_CLOSE_TIME_MS
		};
		this._element = element;
		this._labelEl = SelectorEngine.findOne('.lta-snackbar__label', this._element);
	}

	open() {
		this.clearAutoDismissTimer();
		this.isOpen_ = true;
		this.notifyOpening();
		this._element.classList.remove(CLOSING);
		this._element.classList.add(OPENING);
		this.announce(this._element, this._labelEl);

		// Wait a frame once display is no longer "none", to establish basis for animation
		this.runNextAnimationFrame(() => {
			this._element.classList.add(OPEN);

			this.animationTimer_ = setTimeout(() => {
				const timeoutMs = this.options.openTimeout;
				this.handleAnimationTimerEnd();
				this.notifyOpened();
				if (timeoutMs !== this.numbers.INDETERMINATE) {
					this.autoDismissTimer_ = setTimeout(() => {
						this.close(this.labels.REASON_DISMISS);
					}, timeoutMs);
				}
			}, this.options.openingTimeout);
		});
	}

	close(reason = '') {
		if (!this.isOpen_) {
			// Avoid redundant close calls (and events).
			return;
		}

		cancelAnimationFrame(this.animationFrame_);
		this.animationFrame_ = 0;
		this.clearAutoDismissTimer();

		this.isOpen_ = false;
		this.notifyClosing(reason);
		this._element.classList.add(CLOSING);
		this._element.classList.remove(OPEN);
		this._element.classList.remove(OPENING);

		clearTimeout(this.animationTimer_);
		this.animationTimer_ = setTimeout(() => {
			this.handleAnimationTimerEnd();
			this.notifyClosed(reason);
		}, this.options.closingTimeout);
	}

	announce(ariaEl, labelEl = ariaEl) {
		if (labelEl === null) return;
		announceNotification(ariaEl, labelEl, this.labels.ARIA_LIVE_LABEL_TEXT_ATTR, this.numbers.ARIA_LIVE_DELAY_MS);
	}

	notifyOpening() {
		this.options.onOpening();
	}

	notifyOpened() {
		this.options.onOpen();
	}

	notifyClosing() {
		this.options.onClosing();
	}

	notifyClosed() {
		this.options.onClosed();
	}

	clearAutoDismissTimer() {
		clearTimeout(this.autoDismissTimer_);
		this.autoDismissTimer_ = 0;
	}

	handleAnimationTimerEnd() {
		this.animationTimer_ = 0;
		this._element.classList.remove(OPENING);
		this._element.classList.remove(CLOSING);
	}

	runNextAnimationFrame(callback) {
		cancelAnimationFrame(this.animationFrame_);
		this.animationFrame_ = requestAnimationFrame(() => {
			this.animationFrame_ = 0;
			clearTimeout(this.animationTimer_);
			this.animationTimer_ = setTimeout(callback, 0);
		});
	}
}

export default SnackbarNotification;
